import React, { useId, useMemo, useState } from "react";

/** Tailwind helpers (same palette you use) */
const cardBase =
  "rounded-2xl border border-white/10 bg-gradient-to-b from-[#0E0E0E] to-black";
const goldGrad = "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41]";

/* =========================
   Spark line (SVG, no libs)
   ========================= */

export function SparkLineChart({
  data = [],
  height = 140,
  strokeWidth = 3,
  showDots = true,
  gradient = ["#B17E32", "#F3E681", "#CF9F41"],
}) {
  const uid = useId();
  const width = 560;
  const pad = 10;
  const w = width - pad * 2;
  const h = height - pad * 2;

  const { points } = useMemo(() => {
    const mn = Math.min(...data, 0);
    const mx = Math.max(...data, 100);
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1 || 1)) * w + pad;
      const y = height - pad - ((v - mn) / (mx - mn || 1)) * h;
      return { x, y, value: v };
    });
    return { points: pts };
  }, [data, h, height, w]);

  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, value: 0 });

  return (
    <div className="relative w-full">
      <svg className="w-full" viewBox={`0 0 ${width} ${height}`} role="img">
        <defs>
          <linearGradient id={`goldStroke-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={gradient[0]} />
            <stop offset="50%" stopColor={gradient[1]} />
            <stop offset="100%" stopColor={gradient[2]} />
          </linearGradient>
        </defs>

        <rect
          x="0"
          y="0"
          width={width}
          height={height}
          rx="12"
          className="fill-[#0A0A0A]"
        />
        <polyline
          fill="none"
          stroke={`url(#goldStroke-${uid})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          points={points.map((p) => `${p.x},${p.y}`).join(" ")}
        />
        {showDots &&
          points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="3"
              className="fill-[#F3E681] opacity-80 cursor-pointer"
              onMouseEnter={(e) =>
                setTooltip({ show: true, x: p.x, y: p.y, value: p.value })
              }
              onMouseLeave={() => setTooltip({ ...tooltip, show: false })}
            />
          ))}
      </svg>

      {tooltip.show && (
        <div
          className="absolute bg-black text-yellow-400 text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 30,
            transform: "translate(-50%, 0)",
          }}
        >
          {tooltip.value} users
        </div>
      )}
    </div>
  );
}

/* =========================
   Compact bar chart (divs)
   ========================= */
export function BarChart({
  data = [],
  labels = [],
  maxBarHeight = 120,
  barClass = "bg-yellow-400 rounded-md",
}) {
  const [hovered, setHovered] = useState(null); // index of hovered bar
  const max = Math.max(...data, 100);

  return (
    <div className="grid grid-cols-12 gap-2 items-end w-full relative">
      {data.map((v, i) => {
        const h = Math.max(6, Math.round((v / (max || 1)) * maxBarHeight));
        const label = labels[i] ?? ["M", "T", "W", "T", "F", "S", "S"][i % 7];

        return (
          <div
            key={i}
            className="flex flex-col items-center gap-1 relative"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Tooltip */}
            {hovered === i && (
              <div className="absolute -top-6 bg-black text-white text-[10px] px-1 py-0.5 rounded z-10">
                ${v}
              </div>
            )}

            <div
              className={`${barClass} w-3 sm:w-4 transition-all duration-200`}
              style={{ height: `${h}px` }}
            />
            <span className="hidden sm:block text-[10px] text-zinc-400">
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* =========================
   GraphCard template
   ========================= */
export default function GraphCard({
  className = "",
  title = "This month",
  subtitle = "overview",
  chip, // optional pill text
  type = "spark", // "spark" | "bars"
  data = [],
  labels = [],
  // spark options
  sparkHeight = 140,
  sparkStroke = 3,
  sparkShowDots = true,
  // bars options
  barsMaxHeight = 120,
  // custom header right node
  right,
  // footer content (jsx or string)
  footer,
}) {
  return (
    <div className={`${cardBase} p-3 sm:p-4 ${className}`}>
      <div className="flex items-center justify-between pb-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#F3E681]" />
          <div className="text-sm font-semibold">{title}</div>
          {chip ? (
            <span
              className={`${goldGrad} text-black text-[10px] font-bold px-2 py-0.5 rounded-full`}
            >
              {chip}
            </span>
          ) : null}
        </div>
        {right ?? <span className="text-[11px] text-zinc-400">{subtitle}</span>}
      </div>

      <div className="mt-3">
        {type === "bars" ? (
          <BarChart data={data} labels={labels} maxBarHeight={barsMaxHeight} />
        ) : (
          <SparkLineChart
            data={data}
            height={sparkHeight}
            strokeWidth={sparkStroke}
            showDots={sparkShowDots}
          />
        )}
      </div>

      {footer ? (
        <div className="mt-3 pt-2 border-t border-white/10 text-xs text-zinc-400">
          {footer}
        </div>
      ) : null}
    </div>
  );
}
